import { useNavigate } from "react-router-dom";
import {
  AuthFormData,
  RegFormData,
  RegTouchFields,
  ShowAlertProps,
} from "../../Components/common/types";
import { baseURL, authenticate, register } from "../../config/variables";

const handleLoginCreateFormSubmit = async <
  T extends RegFormData | AuthFormData
>(
  regAuthFormData: T,
  setRegAuthFormData: React.Dispatch<React.SetStateAction<T>>,
  options: {
    setTouched?: React.Dispatch<React.SetStateAction<RegTouchFields>>; // Optional for registration
    validateWrapper?: () => boolean; // Optional for registration
    showAlert: ShowAlertProps["showAlert"];
    showLoader?: ShowAlertProps["showLoader"];
    setAccessToken: (token: string | null) => void; // Pass setAccessToken function
    closeAlert: () => void;
    setIsLoggedOut?: React.Dispatch<React.SetStateAction<boolean>>;
    SESSION_KEY?: string;
    setLoggedInUserName?: React.Dispatch<React.SetStateAction<string>>;
    logout?: (
      isAutoLogout?: boolean,
      title?: string,
      message?: string
    ) => Promise<void>;
    refreshAccessToken?: () => Promise<string | null>;
  },
  isLogin: boolean, //Determine if it's a login or registration
  navigate: ReturnType<typeof useNavigate> // Added navigate for redirection
) => {
  const {
    setTouched,
    validateWrapper,
    showAlert,
    closeAlert,
    showLoader,
    setAccessToken,
    setIsLoggedOut,
    SESSION_KEY,
    setLoggedInUserName,
    logout,
    // refreshAccessToken 
  } = options;

  if (!isLogin && validateWrapper && !validateWrapper()) {
    // console.log("Validation Error: ", regAuthFormData);

    showAlert(
      true,
      "Validation Error",
      "Please fill in all required fields",
      false
    );

    return;
  }

  // Show loader while processing, if defined
  showLoader?.(true);
  try {
    const endpoint = isLogin ? baseURL + authenticate : baseURL + register;

    const response = await fetch(endpoint, {
      method: "POST",
      credentials: "include", // Include cookies
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(regAuthFormData),
    });

    if (!response.ok) {
      const errorData = await response.json();

      if (response.status === 500) {
        showAlert(
          true,
          "Error",
          "Something went wrong on our end. Please try again later.",
          false
        );
      } else {
        //Multiple Login attempts check
        if (response.status === 409 && errorData.redirect) {
          return showAlert(
            true,
            "Session conlict",
            `${errorData.message}. It expires in ${errorData.expiresIn}. You will be redirected to your session after clicking 'OK'.`,
            false,
            () => {
              // console.log("Executing onOk");
              navigate("/dashboard/landing");
              return true;
            }
          );
        } else if (response.status === 409 && errorData.confirmLogin) {
          return showAlert(
            true,
            "Session conlict",
            `${errorData.message} Would you like to continue?.`,
            true,
            undefined,
            async () => {
              //log current user out based on the current cookie in browser
              console.log("Now logging previous user")
              if (logout) {
                await logout(true, "Session Logout", "Previous user is being logged out now");
              }

              // Retry login after logout is successful
              await handleLoginCreateFormSubmit(
                regAuthFormData,
                setRegAuthFormData,
                options,
                isLogin,
                navigate
              );

              return true;
            }
          );
        }

        //backend error from reg or auth controller
        showAlert(true, "Error", errorData.message, false);
      }

      return;
    }

    const data = await response.json();

    //extract logged in username here
    if (setLoggedInUserName) {
      setLoggedInUserName(data.loggedInUserName);
    }

    //set sessionId from backend
    if (SESSION_KEY) {
      localStorage.setItem(SESSION_KEY, data.sessionId);
    }
    setAccessToken(data.accessToken); //store access token to context
    if (setIsLoggedOut) {
      setIsLoggedOut(false);
    }

    //Reset reg or auth form data
    setRegAuthFormData(
      isLogin
        ? ({ email: "", password: "" } as T)
        : ({ name: "", email: "", password: "", confirmPassword: "" } as T)
    );

    // Reset touched fields if provided (only for registration)
    if (!isLogin && setTouched) {
      setTouched({
        name: false,
        email: false,
        password: false,
        confirmPassword: false,
        otp: false,
      });
    }

    // Hide loader if defined and show success alert
    const successMessage = data.message;
    showLoader?.(false);
    showAlert(
      false,
      "Success",
      successMessage,
      false,
      undefined,
      undefined,
      false
    );
    setTimeout(() => {
      navigate(isLogin ? "/dashboard/landing" : "/dashboard/login");
      closeAlert();
    }, 1250); //Redirect after showing alert
  } catch (error) {
    // console.error("Error:", error);
    showAlert(
      true,
      "Error",
      "An unexpected error occurred. Please try again later.",
      false
    );
  } finally {
    // Hide loader regardlessly
    showLoader?.(false);
  }
};

export default handleLoginCreateFormSubmit;
